import React from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Field, IconButton, JsonEditor, LinkButton, optionsTip, Section, SelectInput, TextInput, Toggle, WithInfo } from "./common";
import { ROLE_LABELS } from "./constants";
import { GLOSSARY } from "./glossary";
import { moveItem, setIn } from "./helpers";

const ROLE_TIP = optionsTip("What the field means. Rules and reserved columns find fields by role.", GLOSSARY.fields.roles, ROLE_LABELS);
const TYPE_TIP = optionsTip("Values are converted to this type when they are mapped.", GLOSSARY.fields.types);
const MERGE_TIP = optionsTip("How values combine when several sources fill this field.", GLOSSARY.fields.merge);
const REPRESENTATIVE_RULE_LABELS = { match: "Matches the default variant ref", min: "Lowest value of", max: "Highest value of", first: "First" };

const FieldTable = ({ title, description, fields, onChange, meta, sources, mapName }) => {
    const update = (index, patch) => onChange(fields.map((f, i) => (i === index ? { ...f, ...patch } : f)));
    return (
        <Section title={title} description={description} tip={`${GLOSSARY.fields.model} ${GLOSSARY.fields.coverage}`}>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                            <th className="py-2 pr-2">Name</th>
                            <th className="py-2 pr-2"><WithInfo tip={TYPE_TIP} wide>Type</WithInfo></th>
                            <th className="py-2 pr-2"><WithInfo tip={ROLE_TIP} wide>Role</WithInfo></th>
                            <th className="py-2 pr-2"><WithInfo tip={MERGE_TIP} wide>Merge</WithInfo></th>
                            {/* Source coverage matrix */}
                            {sources.map((s) => (
                                <th key={s.id} className="py-2 px-1 text-center font-mono" title={s.name}>{s.id}</th>
                            ))}
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {fields.map((field, index) => (
                            <tr key={index} className="border-b border-gray-100">
                                <td className="py-1.5 pr-2 min-w-[140px]">
                                    <TextInput monospace className="!py-1" value={field.key} onChange={(key) => update(index, { key: key.replace(/[^A-Za-z0-9_]/g, "") })} />
                                </td>
                                <td className="py-1.5 pr-2">
                                    <SelectInput className="!py-1" value={field.type} options={meta?.field_types || []} onChange={(type) => update(index, { type })} />
                                </td>
                                <td className="py-1.5 pr-2">
                                    <SelectInput
                                        className="!py-1"
                                        value={field.role || ""}
                                        placeholder="—"
                                        options={(meta?.field_roles || []).map((r) => ({ value: r, label: ROLE_LABELS[r] || r }))}
                                        onChange={(role) => update(index, { role: role || undefined })}
                                    />
                                </td>
                                <td className="py-1.5 pr-2">
                                    <SelectInput className="!py-1" value={field.merge || "priority"} options={meta?.merge_policies || ["priority"]} onChange={(merge) => update(index, { merge: merge === "priority" ? undefined : merge })} />
                                </td>
                                {sources.map((s) => {
                                    const mapped = !!s[mapName]?.[field.key];
                                    return (
                                        <td key={s.id} className={`py-1.5 px-1 text-center ${mapped ? "text-green-600" : "text-gray-300"}`} title={mapped ? `Mapped in ${s.name || s.id}` : `Not mapped in ${s.name || s.id}`}>
                                            {mapped ? "✓" : "–"}
                                        </td>
                                    );
                                })}
                                <td className="py-1.5 whitespace-nowrap">
                                    <IconButton title="Move up" disabled={index === 0} onClick={() => onChange(moveItem(fields, index, index - 1))}><ArrowUp size={14} /></IconButton>
                                    <IconButton title="Move down" disabled={index === fields.length - 1} onClick={() => onChange(moveItem(fields, index, index + 1))}><ArrowDown size={14} /></IconButton>
                                    <IconButton title="Remove" onClick={() => onChange(fields.filter((_, i) => i !== index))}><Trash2 size={14} /></IconButton>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <LinkButton onClick={() => onChange([...fields, { key: `field${fields.length + 1}`, type: "string" }])}>
                <Plus size={14} /> Add field
            </LinkButton>
        </Section>
    );
};

// Fields tab: the feed model, roles, merge policies and row-level settings.
const FieldsTab = ({ draft, updateDraft, meta }) => {
    const model = draft.model || { product_fields: [], variant_fields: [] };
    const settings = draft.settings || {};
    const sources = draft.sources || [];
    const representative = settings.representative || meta?.default_config?.settings?.representative || [];
    const variantFields = model.variant_fields || [];

    const setModel = (key, value) => updateDraft((d) => setIn(d, ["model", key], value));
    const setSetting = (path, value) => updateDraft((d) => setIn(d, ["settings", ...path], value));

    return (
        <div className="flex flex-col gap-4 mt-4">
            <p className="text-sm text-gray-600 max-w-4xl">
                The feed model is defined once and every source maps onto it. Roles tell Stride what a field means: the price rule, availability,
                the representative variant and the merge keys all work from roles, never from field names.
            </p>
            <FieldTable
                title="Product fields"
                description="One CSV row per product (unless rows are per variant)."
                fields={model.product_fields || []}
                onChange={(value) => setModel("product_fields", value)}
                meta={meta}
                sources={sources}
                mapName="product_map"
            />
            <FieldTable
                title="Variant fields"
                description="Everything under a product: SKUs, sizes, colours, offers."
                fields={variantFields}
                onChange={(value) => setModel("variant_fields", value)}
                meta={meta}
                sources={sources}
                mapName="variant_map"
            />

            <Section title="Rows and variants">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="One CSV row per" help="Per variant uses variant keys as entity.id." tip={GLOSSARY.fields.rowLevel}>
                        <SelectInput value={settings.row_level || "product"} options={[{ value: "product", label: "Product" }, { value: "variant", label: "Variant" }]} onChange={(v) => setSetting(["row_level"], v)} />
                    </Field>
                    <Field label="Variant order" tip={GLOSSARY.fields.variantOrder}>
                        <SelectInput
                            value={settings.variant_order?.by || "source"}
                            options={[{ value: "source", label: "As in the sources" }, ...variantFields.map((f) => ({ value: f.key, label: `By ${f.key}` }))]}
                            onChange={(by) => setSetting(["variant_order"], by === "source" ? { by } : { by, dir: settings.variant_order?.dir || "asc" })}
                        />
                    </Field>
                    {settings.variant_order?.by && settings.variant_order.by !== "source" && (
                        <Field label="Direction">
                            <SelectInput value={settings.variant_order.dir || "asc"} options={[{ value: "asc", label: "Ascending" }, { value: "desc", label: "Descending" }]} onChange={(dir) => setSetting(["variant_order", "dir"], dir)} />
                        </Field>
                    )}
                </div>
                <Toggle checked={settings.require_variants} onChange={(v) => setSetting(["require_variants"], v)} label="Require variants" help="Drop products that end up with no variants." tip={GLOSSARY.fields.requireVariants} />
            </Section>

            <Section
                title="Representative variant"
                description="Used for thumbnails and other single-variant columns. Rules are tried in order."
                tip={
                    <div className="flex flex-col gap-2">
                        {optionsTip(GLOSSARY.fields.representative, GLOSSARY.fields.representativeRules, REPRESENTATIVE_RULE_LABELS)}
                        {optionsTip("Each rule looks at:", { all: GLOSSARY.variantFilters.all, available: GLOSSARY.variantFilters.available }, { all: "any variant", available: "available variants" })}
                    </div>
                }
            >
                {representative.map((rule, index) => (
                    <div key={index} className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-gray-400 w-5">{index + 1}.</span>
                        <SelectInput
                            className="max-w-[240px]"
                            value={rule.rule}
                            options={Object.entries(REPRESENTATIVE_RULE_LABELS).map(([value, label]) => ({ value, label }))}
                            onChange={(value) => setSetting(["representative"], representative.map((r, i) => (i === index ? { ...r, rule: value } : r)))}
                        />
                        {(rule.rule === "min" || rule.rule === "max") && (
                            <SelectInput
                                className="max-w-[200px]"
                                value={rule.field ? `field:${rule.field}` : `role:${rule.role || "price"}`}
                                options={[
                                    ...(meta?.field_roles || []).map((r) => ({ value: `role:${r}`, label: `${ROLE_LABELS[r] || r} role` })),
                                    ...variantFields.map((f) => ({ value: `field:${f.key}`, label: f.key })),
                                ]}
                                onChange={(value) => {
                                    const [kind, name] = value.split(":");
                                    setSetting(["representative"], representative.map((r, i) => (i === index ? { rule: r.rule, where: r.where, [kind]: name } : r)));
                                }}
                            />
                        )}
                        <SelectInput
                            className="max-w-[180px]"
                            value={rule.where || "all"}
                            options={[{ value: "all", label: "any variant" }, { value: "available", label: "available variants" }]}
                            onChange={(value) => setSetting(["representative"], representative.map((r, i) => (i === index ? { ...r, where: value === "all" ? undefined : value } : r)))}
                        />
                        <IconButton title="Move up" disabled={index === 0} onClick={() => setSetting(["representative"], moveItem(representative, index, index - 1))}><ArrowUp size={14} /></IconButton>
                        <IconButton title="Remove" onClick={() => setSetting(["representative"], representative.filter((_, i) => i !== index))}><Trash2 size={14} /></IconButton>
                    </div>
                ))}
                <LinkButton onClick={() => setSetting(["representative"], [...representative, { rule: "first" }])}>
                    <Plus size={14} /> Add rule
                </LinkButton>
            </Section>

            <details>
                <summary className="text-sm font-medium text-primary cursor-pointer">Edit the model as JSON</summary>
                <JsonEditor rows={20} value={model} onChange={(v) => v && updateDraft((d) => ({ ...d, model: v }))} className="mt-2" />
            </details>
        </div>
    );
};

export default FieldsTab;
